import type { AiState, LlmParam } from './types'

export function filterTenantDropdownOption(
  input: string,
  option?: { label?: unknown; value?: unknown },
): boolean {
  const q = String(input ?? '').trim().toUpperCase()
  if (!q) return true
  const label = String(option?.label ?? '').toUpperCase()
  const val = String(option?.value ?? '').toUpperCase()
  return label.includes(q) || val.includes(q)
}

export function buildAiEditHydrationKey(
  variant: 'ai' | 'db',
  initialRecord: Record<string, unknown> | null | undefined,
): string {
  if (variant !== 'ai' || initialRecord == null) return ''
  const r = initialRecord
  const tid = r.tenant_id ?? r.tenantId
  const tenantKey = Array.isArray(tid)
    ? [...tid].map(String).sort().join(',')
    : String(tid ?? '')
  const rawPid = r.product_id ?? r.productId
  const productKey = Array.isArray(rawPid)
    ? [...rawPid].map(String).sort().join(',')
    : String(rawPid ?? '')
  const idStr = String((r as { id?: unknown }).id ?? '')
  const settings = r.llm_settings
  const settingsKey = Array.isArray(settings)
    ? [...settings]
      .map((x: any) => `${String(x?.key ?? '')}=${String(x?.value ?? '')}`)
      .sort()
      .join(';')
    : ''
  return [
    idStr,
    tenantKey,
    productKey,
    String(r.display_name ?? ''),
    String(r.system_version ?? ''),
    settingsKey,
  ].join('|')
}

export function parseTenantIdFromRecord(
  r: any,
): { scope: 'basic' | 'specific'; tenantIds: string[] } {
  const raw = r?.tenant_id ?? r?.tenantId
  if (raw === 'basic') return { scope: 'basic', tenantIds: ['basic'] }
  if (Array.isArray(raw)) {
    const ids = raw.map(String).map((s: string) => s.trim()).filter(Boolean)
    if (ids.includes('basic')) return { scope: 'basic', tenantIds: ['basic'] }
    return { scope: 'specific', tenantIds: ids }
  }
  if (typeof raw === 'string' && raw.trim()) {
    const v = raw.trim()
    if (v === 'basic') return { scope: 'basic', tenantIds: ['basic'] }
    return { scope: 'specific', tenantIds: [v] }
  }
  return { scope: 'specific', tenantIds: [] }
}

export function parseProductIdFromRecord(
  r: Record<string, unknown> | null | undefined,
): string {
  if (r == null) return ''
  const raw = r.product_id ?? r.productId
  if (Array.isArray(raw)) {
    const first = raw.find((x) => x != null && String(x).trim() !== '')
    return first != null ? String(first).trim() : ''
  }
  if (raw != null && String(raw).trim() !== '') return String(raw).trim()
  return ''
}

export function hydrateAiStateFromRecord(
  r: any,
  isMasterTenant: boolean,
): { aiState: AiState; llmParams: LlmParam[]; selectedTenantIds: string[] } {
  const parsedTenant = parseTenantIdFromRecord(r)

  const aiState: AiState = {
    displayName: r.display_name ?? '',
    systemVersion: r.system_version != null ? String(r.system_version) : '',
    status: r.status ?? '',
    modelType: r.model_type ?? '',
    llmType: r.llm_type ?? '',
    deploymentName: r.deployment_name ?? '',
    modelName: r.model_name ?? '',
    apiType: r.api_type ?? '',
    apiBaseUrl: r.api_baseurl ?? r.api_base_url ?? '',
    apiVersion: r.api_version ?? '',
    apiSecretKey: r.api_secret_key ?? '',
    apiSecretValue: r.api_secret_value ?? '',
    functionCallingEnabled:
      r.fn_calling_enabled === true ? 'true' : r.fn_calling_enabled === false ? 'false' : '',
    tenantScope: isMasterTenant ? parsedTenant.scope : 'specific',
    productId: parseProductIdFromRecord(r),
  }

  const rawSettings = Array.isArray(r.llm_settings) ? r.llm_settings : []
  const llmParams: LlmParam[] = rawSettings.length
    ? rawSettings.map((item: any, i: number) => ({
      id: `p-${i}-${item.key ?? i}`,
      name: item.key ?? '',
      value: item.value != null ? String(item.value) : '',
    }))
    : [{ id: 'p1', name: '', value: '' }]

  const selectedTenantIds = isMasterTenant
    ? parsedTenant.tenantIds.filter((t) => t !== 'basic').map((t) => String(t).trim())
    : []

  return { aiState, llmParams, selectedTenantIds }
}
